import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { getUsersLoggedIn } from '@collections/users';
import { Store } from '@ngrx/store';
import { JobService, CandidateService } from '@shared';
import { HcmJobPostService } from '@shared/invite-to-apply/services/hcm-job-post.service';
import { sendInviteToApply } from '@shared/invite-to-apply/store/invite-to-apply.actions';
import { JobLocation } from '@shared/models/job-location';
import { JobStatusEnum } from '@shared/models/job-statuses';
import { of, combineLatest, Subscription, Observable } from 'rxjs';
import { filter, switchMap, map, shareReplay } from 'rxjs/operators';

export interface InviteToApplyAutocompleteModalData {
  candidateId: number;
}

@Component({
  selector: 'mojo-invite-to-apply-autocomplete-modal',
  templateUrl: './invite-to-apply-autocomplete-modal.component.html',
  styleUrls: ['./invite-to-apply-autocomplete-modal.component.scss'],
})
export class InviteToApplyAutocompleteModalComponent implements OnDestroy {
  jobFormControl: FormControl = this.fb.control(null, [Validators.required]);
  form = new FormGroup({
    job: this.jobFormControl,
  });

  jobs$ = of([]);
  subs = new Subscription();
  link$: Observable<string>;
  linkLoading = false;

  constructor(
    private dialogRef: MatDialogRef<InviteToApplyAutocompleteModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InviteToApplyAutocompleteModalData,
    private fb: FormBuilder,
    private jobService: JobService,
    private store: Store,
    private candidateService: CandidateService,
    private hcmJobPostService: HcmJobPostService,
  ) {
    this.link$ = this.jobFormControl.valueChanges.pipe(
      switchMap((selectedJob) => {
        if (selectedJob == null) {
          return of(null);
        }
        this.linkLoading = true;
        return this.hcmJobPostService.get({ url: `?clientReqId=${selectedJob.clientReqId}` });
      }),
      map((jobPostUrl) => {
        this.linkLoading = false;
        return jobPostUrl?.url;
      }),
      shareReplay(1),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  closeDialog() {
    this.dialogRef.close();
  }

  onInvite() {
    const selectedJob = this.form.controls.job.value;
    this.subs.add(
      combineLatest([
        this.store.select(getUsersLoggedIn),
        this.candidateService.get({ url: `${this.data.candidateId}` }),
        this.jobService.get({ url: `${selectedJob.id}` }),
        this.link$.pipe(filter((url) => !!url)),
      ]).subscribe(([user, candidate, job, url]) => {
        const inviteToApply = {
          candidateName: candidate.firstName + ' ' + candidate.lastName,
          url,
          senderEmail: user.email,
          jobName: job.title,
          jobLocation: this.getJobLocation(job.locations),
          recipientEmail: candidate.emailId,
          candidateId: candidate.id,
          jobId: job.id,
          id: null,
          lastModifiedDate: null,
          fromCandidate: true,
        };
        this.store.dispatch(sendInviteToApply({ inviteToApply }));
        this.closeDialog();
      }),
    );
  }

  onInputChange(input: string) {
    if (this.jobFormControl.value == null) {
      this.jobs$ = this.jobService.autocomplete(input, [JobStatusEnum.Progress], 10);
    }
  }

  private getJobLocation(locations: JobLocation[]): string {
    let locationResult = '';
    locations.forEach((location) => {
      if (location) {
        if (location.city !== null) {
          locationResult = locationResult + location.city + ', ';
        }
        if (location.countryName !== null) {
          locationResult = locationResult + location.countryName + '. ';
        }
      }
    });
    return locationResult.replace(/,\s*$/, '');
  }
}
